import * as yup from 'yup';
import { Tooltip } from '@crazyegginc/hatch';

import { SupportLinks } from '/src/support';
import { SupportLink } from '/src/features/_global/components/SupportLink';
import { countriesQuery } from '/src/features/_global/queries';
import { ADDON_TYPES } from '/src/features/addons/constants';
import { addonListQuery } from '/src/features/addons/queries';
import { goalRefListQuery } from '/src/features/goals/queries';
import { recordingsTagsQuery } from '/src/features/recordings/queries';
import { superLenientUrlRegex } from '/src/utils/regex';
import { FEATURES } from '../_global/constants';
import { CTAUpsellModal } from './components/modals/CTAUpsellModal';
import { GoalUpsellModal } from './components/modals/GoalUpsellModal';
import { SurveyUpsellModal } from './components/modals/SurveyUpsellModal';
import { AUDIENCE_MODES } from './constants';

import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { ReactComponent as CampaignIcon } from '@crazyegginc/hatch/dist/images/icon-campaign-outline.svg';
import { ReactComponent as ClickIcon } from '@crazyegginc/hatch/dist/images/icon-click-outline.svg';
import { ReactComponent as ClockIcon } from '@crazyegginc/hatch/dist/images/icon-clock-outline.svg';
import { ReactComponent as ConversionIcon } from '@crazyegginc/hatch/dist/images/icon-goal-filled.svg';
import { ReactComponent as CountryIcon } from '@crazyegginc/hatch/dist/images/icon-globe-outline.svg';
import { ReactComponent as CurrencyIcon } from '@crazyegginc/hatch/dist/images/icon-currency-outline.svg';
import { ReactComponent as DayOfWeekIcon } from '@crazyegginc/hatch/dist/images/icon-calendar-outline.svg';
import { ReactComponent as DeviceIcon } from '@crazyegginc/hatch/dist/images/icon-mobile-outline.svg';
import { ReactComponent as NewReturningIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-undo.svg';
import { ReactComponent as PagesIcon } from '@crazyegginc/hatch/dist/images/icon-web-pages-outline.svg';
import { ReactComponent as ScreenSizeIcon } from '@crazyegginc/hatch/dist/images/icon-resize.svg';
import { ReactComponent as SessionTagIcon } from '@crazyegginc/hatch/dist/images/icon-tag-outline.svg';
import { ReactComponent as SurveyIcon } from '@crazyegginc/hatch/dist/images/icon-survey-filled.svg';
import { ReactComponent as BrowserIcon } from '@crazyegginc/hatch/dist/images/icon-browser.svg';
import { ReactComponent as VisitorIcon } from '@crazyegginc/hatch/dist/images/icon-user-outline.svg';
import { ReactComponent as ViewedPageIcon } from '@crazyegginc/hatch/dist/images/icon-viewed-page-outline.svg';
import { ReactComponent as ChromeIcon } from '@crazyegginc/hatch/dist/images/logo-browser-chrome-filled.svg';
import { ReactComponent as EdgeIcon } from '@crazyegginc/hatch/dist/images/logo-browser-edge-filled.svg';
import { ReactComponent as FirefoxIcon } from '@crazyegginc/hatch/dist/images/logo-browser-firefox-filled.svg';
import { ReactComponent as IEIcon } from '@crazyegginc/hatch/dist/images/logo-browser-ie-filled.svg';
import { ReactComponent as OperaIcon } from '@crazyegginc/hatch/dist/images/logo-browser-opera-filled.svg';
import { ReactComponent as SafariIcon } from '@crazyegginc/hatch/dist/images/logo-browser-safari-filled.svg';
import { ReactComponent as AndroidIcon } from '@crazyegginc/hatch/dist/images/logo-os-android-filled.svg';
import { ReactComponent as AppleIcon } from '@crazyegginc/hatch/dist/images/logo-os-apple.svg';
import { ReactComponent as LinuxIcon } from '@crazyegginc/hatch/dist/images/logo-os-linux-filled.svg';
import { ReactComponent as MacOSIcon } from '@crazyegginc/hatch/dist/images/logo-os-macos-filled.svg';
import { ReactComponent as WindowsIcon } from '@crazyegginc/hatch/dist/images/logo-os-windows-filled.svg';
import { ReactComponent as DesktopIcon } from '@crazyegginc/hatch/dist/images/icon-desktop-outline.svg';
import { ReactComponent as TabletIcon } from '@crazyegginc/hatch/dist/images/icon-tablet-outline.svg';
import { ReactComponent as MobileIcon } from '@crazyegginc/hatch/dist/images/icon-mobile-outline.svg';

export const CATEGORIES = {
  VISITOR_INFO: 'Visitor Info',
  SESSION_INFO: 'Session Info',
  ACTIVITY: 'Activity',
  PATH: 'Path',
  AD_CAMPAIGN: 'Ad Campaign',
};

const IS_COMPARISONS = [
  { value: 'is', json: 'any', positive: true },
  { value: 'is not', json: 'any', positive: false },
];
const IS_COMPARISON = [{ value: 'is', json: 'any', positive: true }];
const DID_COMPARISONS = [
  { value: 'did', json: 'any', positive: true },
  { value: 'did not do', json: 'any', positive: false },
];
const NUM_COMPARISONS = [
  { value: 'greater than', json: 'greater_than', positive: true },
  { value: 'less than', label: 'less    than', json: 'less_than', positive: true },
  { value: 'equals', json: 'equals', positive: true },
  { value: 'at least', json: 'at_least', positive: true },
  { value: 'at most', json: 'at_most', positive: true },
  { value: 'between', json: 'between', positive: true },
];
const DURATION_COMPARISONS = [
  { value: 'at least', json: 'at_least', positive: true },
  { value: 'at most', json: 'at_most', positive: true },
  { value: 'greater than', json: 'greater_than', positive: true },
  { value: 'less than', label: 'less    than', json: 'less_than', positive: true },
  { value: 'equals', json: 'equals', positive: true },
  { value: 'between', json: 'between', positive: true },
];
const CURRENCY_CODE_REGEX = /^[A-Z]{3}$/;
const DEVICE_OPTIONS = [
  { label: 'Desktop', value: 'desktop', icon: DesktopIcon },
  { label: 'Tablet', value: 'tablet', icon: TabletIcon },
  { label: 'Mobile', value: 'phone', icon: MobileIcon },
];
const BROWSER_OPTIONS = [
  { label: 'Chrome', value: 'chrome', icon: ChromeIcon },
  { label: 'Firefox', value: 'firefox', icon: FirefoxIcon },
  { label: 'Safari', value: 'safari', icon: SafariIcon },
  { label: 'Internet Explorer', value: 'ie', icon: IEIcon },
  { label: 'Edge', value: 'edge', icon: EdgeIcon },
  { label: 'Opera', value: 'opera', icon: OperaIcon },
  { label: 'Other', value: 'unknown' },
];
const OS_OPTIONS = [
  { label: 'Android', value: 'Android', icon: AndroidIcon },
  { label: 'iOS', value: 'iOS', icon: AppleIcon },
  { label: 'Linux', value: 'Linux', icon: LinuxIcon },
  { label: 'macOS', value: 'macOS', icon: MacOSIcon },
  { label: 'Windows', value: 'Windows', icon: WindowsIcon },
  { label: 'Windows Phone', value: 'Windows Phone', icon: WindowsIcon },
  { label: 'Other', value: 'Other' },
];
const DAY_OF_WEEK_OPTIONS = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];
const NEW_OR_RETURNING_OPTIONS = [
  { label: 'New visitors', value: 'new' },
  { label: 'Returning visitors', value: 'returning' },
];
const DURATION_UNIT_OPTIONS = [{ value: 'seconds' }, { value: 'minutes' }, { value: 'hours' }, { value: 'days' }];
const URL_EXTRA = (
  <div className="flex h-[35px] items-center ml-2.5">
    <Tooltip
      containerStyle={{ zIndex: 100000 }}
      tooltipContent={
        <div className="max-w-[250px] text-left">
          Use * as a wildcard to match multiple URLs.
          <br />
          <br /> Examples:
          <ul className="ml-5 list-outside list-disc">
            <li>
              <code className="rounded-sm bg-mako-500 px-px text-center">mysite.com/blog/*</code> matches all blog pages
            </li>
            <li>
              <code className="rounded-sm bg-mako-500 px-px text-center">mysite.com/product/*/reviews</code> matches any
              product review page
            </li>
          </ul>
        </div>
      }
    >
      <InfoIcon
        aria-label="information on URL wildcards"
        className="h-4 w-4 shrink-0 fill-current text-dodger-blue-300"
      />
    </Tooltip>
  </div>
);

export const AUDIENCE_METADATA = {
  visitor_id: {
    label: 'Visitor',
    secondaryLabel: '(email, name or ID)',
    icon: VisitorIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. user@email.com',
    validation: createTextValidation(),
    defaultValue: [''],
    extra: (
      <div className="flex h-[35px] items-center ml-2.5">
        <Tooltip
          containerStyle={{ zIndex: 100000 }}
          interactive={true}
          tooltipContent={
            <div className="max-w-[180px]">
              Search for Specific visitors with our{' '}
              <SupportLink className="text-link text-2xs" href={SupportLinks.sessions.visitorIdentifier}>
                Visitor ID script
              </SupportLink>
              .
            </div>
          }
        >
          <InfoIcon
            aria-label="information on URL wildcards"
            className="h-4 w-4 shrink-0 fill-current text-dodger-blue-300"
          />
        </Tooltip>
      </div>
    ),
  },
  browser: {
    label: 'Browser',
    icon: BrowserIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'checkbox',
    options: BROWSER_OPTIONS,
    comparisons: IS_COMPARISONS,
    validation: createCheckboxValidation({
      options: BROWSER_OPTIONS,
      requiredMessage: 'Please select at least one browser.',
    }),
  },
  country: {
    label: 'Country',
    icon: CountryIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'asyncSelect',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'Select a country',
    validation: createAsyncSelectValidation({
      requiredMessage: 'Please select a country.',
    }),
    defaultValue: [''],
    asyncOptions: {
      query: countriesQuery,
      getOptions: ({ data }) => data?.countries?.map((country) => ({ value: country.code, label: country.name })) ?? [],
    },
  },
  device: {
    label: 'Device',
    icon: DeviceIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'checkbox',
    options: DEVICE_OPTIONS,
    comparisons: IS_COMPARISONS,
    validation: createCheckboxValidation({
      options: DEVICE_OPTIONS,
      requiredMessage: 'Please select at least one device type.',
    }),
  },
  os: {
    label: 'Operating System',
    icon: VisitorIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'checkbox',
    options: OS_OPTIONS,
    comparisons: IS_COMPARISONS,
    validation: createCheckboxValidation({
      options: OS_OPTIONS,
      requiredMessage: 'Please select at least one operating system.',
    }),
  },
  new_or_returning: {
    label: 'New or Returning',
    icon: NewReturningIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'radio',
    options: NEW_OR_RETURNING_OPTIONS,
    comparisons: IS_COMPARISON,
    hiddenComparison: true,
    validation: yup.object().shape({
      comparison: yup
        .string()
        .required()
        .oneOf(IS_COMPARISON.map((item) => item.value)),
      values: yup.string().oneOf(NEW_OR_RETURNING_OPTIONS.map((option) => option.value)),
    }),
    defaultValue: NEW_OR_RETURNING_OPTIONS[0].value,
  },
  screen_width: {
    label: 'Screen Width',
    icon: ScreenSizeIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'number',
    comparisons: NUM_COMPARISONS,
    placeholder: 'e.g 2',
    validation: createNumberValidation(),
    defaultValue: { amount: '', max: '' },
    serializeValue: serializeNumberValue,
    deserializeValue: deserializeNumberValue,
  },
  screen_height: {
    label: 'Screen Height',
    icon: ScreenSizeIcon,
    category: CATEGORIES.VISITOR_INFO,
    type: 'number',
    comparisons: NUM_COMPARISONS,
    placeholder: 'e.g 2',
    validation: createNumberValidation(),
    defaultValue: { amount: '', max: '' },
    serializeValue: serializeNumberValue,
    deserializeValue: deserializeNumberValue,
  },
  duration: {
    label: 'Duration',
    icon: ClockIcon,
    category: CATEGORIES.SESSION_INFO,
    type: 'duration',
    comparisons: DURATION_COMPARISONS,
    placeholder: 'e.g 30',
    unitOptions: DURATION_UNIT_OPTIONS,
    validation: createDurationValidation(),
    defaultValue: { amount: '', max: '', unit: DURATION_UNIT_OPTIONS[0].value },
    serializeValue: serializeDurationValue,
    deserializeValue: deserializeDurationValue,
  },
  tags: {
    label: 'Tags',
    tooltipLabel: 'tag',
    icon: SessionTagIcon,
    category: CATEGORIES.SESSION_INFO,
    type: 'asyncSelect',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    multiAnd: true,
    placeholder: 'Select a tag',
    validation: createAsyncSelectValidation({
      requiredMessage: 'Please select a tag.',
    }),
    defaultValue: [''],
    asyncOptions: {
      query: ({ selectedSite }) =>
        recordingsTagsQuery({
          fragment: '',
          site: selectedSite?.id,
          limit: 1000,
        }),
      enabled: ({ selectedSite }) => Boolean(selectedSite?.id),
      getOptions: ({ data }) => data?.recordingsTags?.map((tag) => ({ value: tag, label: tag })) ?? [],
    },
    extra: (
      <div className="flex h-[35px] items-center ml-2.5">
        <Tooltip
          containerStyle={{ zIndex: 100000 }}
          interactive={true}
          tooltipContent={
            <div className="max-w-[140px]">
              <SupportLink className="text-link text-2xs" href={SupportLinks.recordings.autoGeneratedTags}>
                Learn more
              </SupportLink>{' '}
              about our auto generated tags.
            </div>
          }
        >
          <InfoIcon
            aria-label="information on URL wildcards"
            className="h-4 w-4 shrink-0 fill-current text-dodger-blue-300"
          />
        </Tooltip>
      </div>
    ),
  },
  day_of_week: {
    label: 'Day of Week',
    icon: DayOfWeekIcon,
    category: CATEGORIES.SESSION_INFO,
    type: 'checkbox',
    options: DAY_OF_WEEK_OPTIONS,
    comparisons: IS_COMPARISONS,
    validation: createCheckboxValidation({
      options: DAY_OF_WEEK_OPTIONS,
      requiredMessage: 'Please select at least one day of the week.',
    }),
  },
  max_worth: {
    label: 'Max $',
    icon: CurrencyIcon,
    category: CATEGORIES.SESSION_INFO,
    type: 'money',
    comparisons: NUM_COMPARISONS,
    placeholder: 'e.g 10',
    validation: createMoneyValidation(),
    defaultValue: { amount: '', max: '', currency: 'USD' },
    serializeValue: serializeMoneyValue,
    deserializeValue: deserializeMoneyValue,
  },
  goal_conversions: {
    label: 'Conversions',
    tooltipLabel: 'conversion',
    icon: ConversionIcon,
    category: CATEGORIES.ACTIVITY,
    type: 'asyncSelect',
    comparisons: DID_COMPARISONS,
    multiOr: true,
    multiAnd: true,
    placeholder: 'Select a conversion',
    validation: createAsyncSelectValidation({
      comparisons: DID_COMPARISONS,
      requiredMessage: 'Please select a conversion.',
      valueSchema: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .typeError('Please select a conversion.')
        .required('Please select a conversion.'),
    }),
    defaultValue: [''],
    asyncOptions: {
      query: ({ selectedSite }) => goalRefListQuery({ siteId: selectedSite?.id }),
      enabled: ({ selectedSite }) => Boolean(selectedSite?.id),
      getOptions: ({ data }) =>
        data?.goalRefList
          ?.map((goal) => ({ value: goal.id, label: goal.name || '[Unnamed conversion]' }))
          .sort((left, right) => left.label.localeCompare(right.label)) ?? [],
    },
    filteringFeatureAccess: { permission: 'view', feature: FEATURES.GOALS, upsellModal: GoalUpsellModal },
  },
  clicked_cta: {
    label: 'Clicked CTA',
    tooltipLabel: 'CTA',
    icon: ClickIcon,
    category: CATEGORIES.ACTIVITY,
    type: 'asyncSelect',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    multiAnd: true,
    placeholder: 'Select a CTA',
    validation: createAsyncSelectValidation({
      requiredMessage: 'Please select a CTA.',
    }),
    defaultValue: [''],
    asyncOptions: createAddonAsyncOptions(ADDON_TYPES.CTA),
    filteringFeatureAccess: { permission: 'view', feature: FEATURES.CTAS, upsellModal: CTAUpsellModal },
  },
  answered_survey: {
    label: 'Answered Survey',
    tooltipLabel: 'survey',
    icon: SurveyIcon,
    category: CATEGORIES.ACTIVITY,
    type: 'asyncSelect',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    multiAnd: true,
    placeholder: 'Select a Survey',
    validation: createAsyncSelectValidation({
      requiredMessage: 'Please select a Survey.',
    }),
    defaultValue: [''],
    asyncOptions: createAddonAsyncOptions(ADDON_TYPES.SURVEY),
    filteringFeatureAccess: { permission: 'view', feature: FEATURES.SURVEYS, upsellModal: SurveyUpsellModal },
  },
  landing_page: {
    label: 'Landing Page',
    tooltipLabel: 'page',
    icon: ViewedPageIcon,
    category: CATEGORIES.PATH,
    type: 'url',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. mysite.com/some-page/*',
    validation: createURLvalidation(),
    defaultValue: [''],
    serializeValue: serializeURLValue,
    deserializeValue: deserializeURLValue,
    extra: URL_EXTRA,
  },
  visited_page: {
    label: 'Visited Page',
    tooltipLabel: 'page',
    icon: ViewedPageIcon,
    category: CATEGORIES.PATH,
    type: 'url',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    multiAnd: true,
    placeholder: 'e.g. mysite.com/some-page/*',
    validation: createURLvalidation(),
    defaultValue: [''],
    serializeValue: serializeURLValue,
    deserializeValue: deserializeURLValue,
    extra: URL_EXTRA,
  },
  pages_viewed: {
    label: 'No. of Pages Viewed',
    icon: PagesIcon,
    category: CATEGORIES.PATH,
    type: 'number',
    comparisons: NUM_COMPARISONS,
    placeholder: 'e.g 2',
    validation: createNumberValidation(),
    defaultValue: { amount: '', max: '' },
    serializeValue: serializeNumberValue,
    deserializeValue: deserializeNumberValue,
  },
  referrer: {
    label: 'Referrer',
    icon: ViewedPageIcon,
    category: CATEGORIES.PATH,
    type: 'url',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. mysite.com/some-page/*',
    validation: createURLvalidation(),
    defaultValue: [''],
    serializeValue: serializeURLValue,
    deserializeValue: deserializeURLValue,
    extra: URL_EXTRA,
  },
  utm_source: {
    label: 'UTM Source',
    icon: CampaignIcon,
    category: CATEGORIES.AD_CAMPAIGN,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. google',
    validation: createTextValidation(),
    defaultValue: [''],
  },
  utm_medium: {
    label: 'UTM Medium',
    icon: CampaignIcon,
    category: CATEGORIES.AD_CAMPAIGN,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. email',
    validation: createTextValidation(),
    defaultValue: [''],
  },
  utm_term: {
    label: 'UTM Term',
    icon: CampaignIcon,
    category: CATEGORIES.AD_CAMPAIGN,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. email-subscription',
    validation: createTextValidation(),
    defaultValue: [''],
  },
  utm_content: {
    label: 'UTM Content',
    icon: CampaignIcon,
    category: CATEGORIES.AD_CAMPAIGN,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. sidebar-banner',
    validation: createTextValidation(),
    defaultValue: [''],
  },
  utm_campaign: {
    label: 'UTM Name',
    icon: CampaignIcon,
    category: CATEGORIES.AD_CAMPAIGN,
    type: 'text',
    comparisons: IS_COMPARISONS,
    multiOr: true,
    placeholder: 'e.g. winter-sale',
    validation: createTextValidation(),
    defaultValue: [''],
  },
};

function serializeURLValue(values = []) {
  return values.map((value) => ({ url: value }));
}

function deserializeURLValue(values = []) {
  return values.map((value) => value?.url ?? '');
}

function createAddonAsyncOptions(type) {
  return {
    query: ({ selectedSite }) =>
      addonListQuery({
        siteId: selectedSite?.id,
        filter: {
          limit: -1,
          query: null,
          type,
        },
        minimal: true,
      }),
    enabled: ({ selectedSite }) => Boolean(selectedSite?.id),
    getOptions: ({ data, mode }) =>
      data?.addonList?.list
        ?.filter((addon) => addon.publishedAt && (mode === AUDIENCE_MODES.FILTERING || addon.active))
        .map((addon) => ({ value: addon.id, label: addon.name }))
        .sort((left, right) => left.label.localeCompare(right.label)) ?? [],
  };
}

function serializeNumberValue(values = [], comparisonJson) {
  if (comparisonJson === 'between') {
    return {
      min: Number(values.amount),
      max: Number(values.max),
    };
  }

  return Number(values.amount);
}

function deserializeNumberValue(value, comparisonJson) {
  if (comparisonJson === 'between') {
    return { amount: value?.min ?? '', max: value?.max ?? '' };
  }

  return { amount: value ?? '', max: '' };
}

function serializeMoneyValue(values = [], comparisonJson) {
  if (comparisonJson === 'between') {
    return {
      min: Number(values.amount),
      max: Number(values.max),
      currency: values.currency,
    };
  }

  return {
    number: Number(values.amount),
    currency: values.currency,
  };
}

function deserializeMoneyValue(value, comparisonJson) {
  if (comparisonJson === 'between') {
    return { amount: value?.min ?? '', max: value?.max ?? '', currency: value?.currency ?? '' };
  }

  return { amount: value?.number ?? '', max: '', currency: value?.currency ?? '' };
}

function serializeDurationValue(values = {}, comparisonJson) {
  if (comparisonJson === 'between') {
    return {
      min: Number(values.amount),
      max: Number(values.max),
      unit: values.unit,
    };
  }

  return {
    count: Number(values.amount),
    unit: values.unit,
  };
}

function deserializeDurationValue(value, comparisonJson) {
  if (comparisonJson === 'between') {
    return { amount: value?.min ?? '', max: value?.max ?? '', unit: value?.unit ?? DURATION_UNIT_OPTIONS[0].value };
  }

  return { amount: value?.count ?? '', max: '', unit: value?.unit ?? DURATION_UNIT_OPTIONS[0].value };
}

function createURLvalidation({ comparisons = IS_COMPARISONS } = {}) {
  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup
      .array()
      .of(
        yup
          .string()
          .required('Please provide a URL.')
          .test('is valid url', 'Please provide a valid URL.', (value) => {
            if (!value) return false;
            return value.match(superLenientUrlRegex);
          }),
      )
      .min(1, 'Please add at least one value.'),
  });
}

function createCheckboxValidation({ options, comparisons = IS_COMPARISONS, requiredMessage }) {
  let values = yup
    .array()
    .of(yup.string().oneOf(options.map((option) => option.value)))
    .required(requiredMessage)
    .min(1, requiredMessage);

  if (options.length > 1) {
    values = values.max(options.length - 1, 'Remove this condition instead of selecting all options.');
  }

  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values,
  });
}

function createTextValidation({ comparisons = IS_COMPARISONS, requiredMessage = 'Please provide a value.' } = {}) {
  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup.array().of(yup.string().required(requiredMessage)).min(1, 'Please add at least one value.'),
  });
}

function createAsyncSelectValidation({
  comparisons = IS_COMPARISONS,
  requiredMessage = 'Please select a value.',
  valueSchema = yup.string().required(requiredMessage),
} = {}) {
  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup.array().of(valueSchema).min(1, 'Please add at least one value.'),
  });
}

function createNumberValidation({ comparisons = NUM_COMPARISONS, requiredMessage = 'Please provide a number.' } = {}) {
  const nonNegativeMessage = 'Please provide a number greater than or equal to 0.';
  const number = yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .typeError(requiredMessage)
    .required(requiredMessage)
    .min(0, nonNegativeMessage);

  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup.object().when('comparison', {
      is: 'between',
      then: yup
        .object()
        .shape({
          amount: number,
          max: number,
        })
        .required('Please provide a minimum and maximum.')
        .test('max-greater-than-min', 'Max must be greater than min.', function (values) {
          const minValue = Number(values?.amount);
          const maxValue = Number(values?.max);
          if (values?.amount == null || values?.max == null || Number.isNaN(minValue) || Number.isNaN(maxValue)) {
            return true;
          }

          return maxValue > minValue || this.createError({ path: `${this.path}.max` });
        }),
      otherwise: yup
        .object()
        .shape({
          amount: number,
        })
        .required(requiredMessage),
    }),
  });
}

function createMoneyValidation({ comparisons = NUM_COMPARISONS, requiredMessage = 'Please provide a number.' } = {}) {
  const nonNegativeMessage = 'Please provide a number greater than or equal to 0.';

  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup.object().when('comparison', {
      is: 'between',
      then: createMoneyValuesValidation({
        requireMax: true,
        requiredMessage,
        nonNegativeMessage,
      }).test('max-greater-than-min', 'Max must be greater than min.', function (values) {
        if (getNumberValidationMessage(values?.amount, requiredMessage, nonNegativeMessage)) return true;
        if (getNumberValidationMessage(values?.max, requiredMessage, nonNegativeMessage)) return true;

        return Number(values.max) > Number(values.amount) || this.createError({ path: `${this.path}.max` });
      }),
      otherwise: createMoneyValuesValidation({
        requireMax: false,
        requiredMessage,
        nonNegativeMessage,
      }),
    }),
  });
}

function createDurationValidation({
  comparisons = DURATION_COMPARISONS,
  requiredMessage = 'Please provide a number.',
} = {}) {
  const nonNegativeMessage = 'Please provide a number greater than or equal to 0.';

  return yup.object().shape({
    comparison: yup
      .string()
      .required()
      .oneOf(comparisons.map((item) => item.value)),
    values: yup.object().when('comparison', {
      is: 'between',
      then: createDurationValuesValidation({
        requireMax: true,
        requiredMessage,
        nonNegativeMessage,
      }).test('max-greater-than-min', 'Max must be greater than min.', function (values) {
        if (getNumberValidationMessage(values?.amount, requiredMessage, nonNegativeMessage)) return true;
        if (getNumberValidationMessage(values?.max, requiredMessage, nonNegativeMessage)) return true;

        return Number(values.max) > Number(values.amount) || this.createError({ path: `${this.path}.max` });
      }),
      otherwise: createDurationValuesValidation({
        requireMax: false,
        requiredMessage,
        nonNegativeMessage,
      }),
    }),
  });
}

function createDurationValuesValidation({ requireMax, requiredMessage, nonNegativeMessage }) {
  const unitRequiredMessage = 'Please select a unit.';
  const unitInvalidMessage = 'Please select a valid unit.';
  const durationUnits = DURATION_UNIT_OPTIONS.map((option) => option.value);

  let schema = yup.object().required('Please provide a duration.');

  for (const amountField of requireMax ? ['amount', 'max'] : ['amount']) {
    schema = schema.test(`duration-number-${amountField}`, requiredMessage, function (values) {
      const error = getNumberValidationMessage(values?.[amountField], requiredMessage, nonNegativeMessage);
      return !error || this.createError({ path: `${this.path}.${amountField}`, message: error });
    });
  }

  return schema.test('duration-unit', unitRequiredMessage, function (values) {
    const unit = values?.unit;

    if (!unit) {
      return this.createError({ path: `${this.path}.unit`, message: unitRequiredMessage });
    }

    return durationUnits.includes(unit) || this.createError({ path: `${this.path}.unit`, message: unitInvalidMessage });
  });
}

function createMoneyValuesValidation({ requireMax, requiredMessage, nonNegativeMessage }) {
  const currencyRequiredMessage = 'Please select a currency.';
  const currencyInvalidMessage = 'Please select a valid currency.';

  let schema = yup.object().required('Please provide an amount and currency.');

  for (const amountField of requireMax ? ['amount', 'max'] : ['amount']) {
    schema = schema.test(`money-number-${amountField}`, requiredMessage, function (values) {
      const error = getNumberValidationMessage(values?.[amountField], requiredMessage, nonNegativeMessage);
      return !error || this.createError({ path: `${this.path}.${amountField}`, message: error });
    });
  }

  return schema.test('money-currency', currencyRequiredMessage, function (values) {
    const currency = values?.currency;

    if (!currency) {
      return this.createError({ path: `${this.path}.currency`, message: currencyRequiredMessage });
    }

    return (
      CURRENCY_CODE_REGEX.test(currency) ||
      this.createError({ path: `${this.path}.currency`, message: currencyInvalidMessage })
    );
  });
}

function getNumberValidationMessage(value, requiredMessage, nonNegativeMessage) {
  if (value == null || (typeof value === 'string' && value.trim() === '')) {
    return requiredMessage;
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return requiredMessage;
  }

  if (numberValue < 0) {
    return nonNegativeMessage;
  }

  return null;
}

function getConditionSchema(condition) {
  const criteria = condition.criteria;

  const { validation, multiAnd } = AUDIENCE_METADATA[criteria];
  let rows = yup.array().of(validation).required('Please add at least one row.').min(1, 'Please add at least one row.');

  if (!multiAnd) {
    rows = rows.max(1, 'You can only add this criteria once.');
  }

  return yup.object().shape({
    criteria: yup.string().required().oneOf(Object.keys(AUDIENCE_METADATA)),
    rows,
  });
}

export const validationSchema = yup.object().shape({
  conditions: yup
    .array()
    .of(
      yup.lazy((condition) => {
        return getConditionSchema(condition);
      }),
    )
    .required('Please add at least one condition.')
    .min(1, 'Please add at least one condition.'),
});
